// ==================== KEYWORD BOTS SERVICE ====================

import { apiService } from './api';

// ==================== TYPES ====================

export type KeywordMatchType = 'EXACT' | 'CONTAINS' | 'STARTS_WITH' | 'ENDS_WITH';
export type BotReplyType = 'TEXT' | 'TEMPLATE';

export interface QuickReplyButton {
  id: string;
  title: string;
}

export interface KeywordBot {
  id: string;
  name: string;
  description?: string;
  keywords: string[];
  matchType: KeywordMatchType;
  caseSensitive: boolean;
  replyType: BotReplyType;
  replyContent?: string;
  quickReplyButtons?: QuickReplyButton[];
  templateId?: string;
  isActive: boolean;
  priority: number;
  cooldownMinutes?: number;
  enableSchedule: boolean;
  scheduleStart?: string;
  scheduleEnd?: string;
  scheduleDays?: number[];
  timezone?: string;
  welcomeInactivityHours?: number;
  createdAt: string;
  updatedAt: string;
  template?: {
    id: string;
    name: string;
    status: string;
    bodyText?: string;
  };
  _count?: {
    triggers: number;
  };
}

export interface CreateKeywordBotData {
  name: string;
  description?: string;
  keywords: string[];
  matchType: KeywordMatchType;
  caseSensitive?: boolean;
  replyType: BotReplyType;
  replyContent?: string;
  quickReplyButtons?: QuickReplyButton[];
  templateId?: string;
  isActive?: boolean;
  priority?: number;
  cooldownMinutes?: number;
  enableSchedule?: boolean;
  scheduleStart?: string;
  scheduleEnd?: string;
  scheduleDays?: number[];
  timezone?: string;
  welcomeInactivityHours?: number;
}

export type UpdateKeywordBotData = Partial<CreateKeywordBotData>;

export interface QueryKeywordBotsParams {
  search?: string;
  isActive?: boolean;
  matchType?: KeywordMatchType;
  page?: number;
  limit?: number;
}

export interface KeywordBotsResponse {
  items: KeywordBot[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface TriggerStats {
  totalTriggers: number;
  triggersByDay: Array<{ triggeredAt: string; _count: number }>;
  topKeywords: Array<{ matchedKeyword: string; _count: number }>;
}

// ==================== SERVICE ====================

export const keywordBotsService = {
  async getAll(params?: QueryKeywordBotsParams): Promise<KeywordBotsResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.search) queryParams.append('search', params.search);
    if (params?.isActive !== undefined) queryParams.append('isActive', String(params.isActive));
    if (params?.matchType) queryParams.append('matchType', params.matchType);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const queryString = queryParams.toString();
    const url = queryString ? `/customer/keyword-bots?${queryString}` : '/customer/keyword-bots';

    const response = await apiService.get(url);
    
    type MetaType = { total: number; page: number; limit: number; totalPages: number };
    const data = response.data as KeywordBot[] | { items: KeywordBot[]; meta: MetaType };
    const responseAny = response as unknown as { meta?: MetaType };
    return {
      items: Array.isArray(data) ? data : (data?.items || []),
      meta: responseAny.meta || (data && 'meta' in data ? data.meta : { total: 0, page: 1, limit: 20, totalPages: 0 }),
    };
  },

  /**
   * Fetch every bot matching the filters, following pagination to the last page.
   *
   * The API paginates this endpoint and caps `limit` at 100 (PAGINATION.MAX_LIMIT),
   * so a single request can never be relied on to return everything. Page 1 tells
   * us how many pages there are; any remainder is fetched in parallel.
   */
  async getAllPages(
    params?: Omit<QueryKeywordBotsParams, 'page' | 'limit'>,
  ): Promise<KeywordBot[]> {
    const PAGE_SIZE = 100;

    const firstPage = await this.getAll({ ...params, page: 1, limit: PAGE_SIZE });
    const { totalPages } = firstPage.meta;

    if (totalPages <= 1) {
      return firstPage.items;
    }

    const remainingPages = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) =>
        this.getAll({ ...params, page: index + 2, limit: PAGE_SIZE }),
      ),
    );

    return remainingPages.reduce(
      (all, page) => all.concat(page.items),
      firstPage.items,
    );
  },

  async getById(id: string): Promise<KeywordBot> {
    const response = await apiService.get(`/customer/keyword-bots/${id}`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch keyword bot');
    }
    return response.data as KeywordBot;
  },

  async getStats(id: string, days?: number): Promise<TriggerStats> {
    const url = days 
      ? `/customer/keyword-bots/${id}/stats?days=${days}` 
      : `/customer/keyword-bots/${id}/stats`;
    const response = await apiService.get(url);
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch bot stats');
    }
    return response.data as TriggerStats;
  },

  async create(data: CreateKeywordBotData): Promise<KeywordBot> {
    const response = await apiService.post('/customer/keyword-bots', data);
    if (!response.success) {
      throw new Error(response.message || 'Failed to create keyword bot');
    }
    return response.data as KeywordBot;
  },

  async update(id: string, data: UpdateKeywordBotData): Promise<KeywordBot> {
    const response = await apiService.put(`/customer/keyword-bots/${id}`, data);
    if (!response.success) {
      throw new Error(response.message || 'Failed to update keyword bot');
    }
    return response.data as KeywordBot;
  },

  async toggleActive(id: string): Promise<KeywordBot> {
    const response = await apiService.patch(`/customer/keyword-bots/${id}/toggle`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to toggle bot status');
    }
    return response.data as KeywordBot;
  },

  async delete(id: string): Promise<void> {
    const response = await apiService.delete(`/customer/keyword-bots/${id}`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete keyword bot');
    }
  },
};
